// Import react elements
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Import core functions
import { registerNewAccount } from "api/APICoreCalls";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useAppConfig } from "global/hooks/useAppConfig";

// Import components
import CreateButton from "global/components/CreateButton";

// Import icons
import { ShowAllIcon } from "global/strich_icons/StrichIcons";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { SetCookie } from "global/utilities/Cookies";

// Import css style sheet
import styles from "../LoginPage.module.css";



const RegisterForm = ({ onSwitch }) => {



    // Set navigate to useNavigate() which provides a function to programmatically navigate between routes
    const navigate = useNavigate();

    // Get the current app environment config
    const appConfig = useAppConfig();

    // Store whether each password field should be visible
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Record when the registration form was first displayed for basic bot timing detection
    const registrationStarted = useRef(performance.now());

    // Extract form-related state and functions from the `useFormHandler` custom hook
    const {

        formValues,     // Stores the current values of form inputs
        handleChange,   // Function to update form values when inputs change
        handleSubmit,   // Function to handle form submission logic
        fieldErrors,    // Object storing validation errors for individual fields
        formError,      // General error message (e.g. API errors)
        loading,        // Boolean indicating whether the form is submitting
        apiResponse,    // Stores the response from the API after submission

    } = useFormHandler({

        // Default values for the form fields
        initialValues: {

            username: "",
            email: "",
            password: "",
            confirmPassword: "",
            contact_website: "",


        },

        // API function to be called on form submission - also add the elapsed form time for basic bot detection
        apiCall: (values) => registerNewAccount({
            ...values,
            form_elapsed_ms: Math.round(performance.now() - registrationStarted.current),
        }),


        // Validation function to check the form fields before submission
        validate: (values) => {

            // Set errors to empty
            const errors = {};

            // Validation rules
            if (!values.email.includes("@")) errors.email = "Invalid email";
            if (values.password.length < 6) errors.password = "Password must be at least 6 characters";
            if (values.password !== values.confirmPassword) errors.confirmPassword = "Passwords must match";

            // Return validation errors (if any)
            return errors;
        },
    });



    // useEffect runs whenever `apiResponse` changes
    useEffect(() => {

        // Check if there is a response
        if (apiResponse) {

            // Check if it is successful
            if (apiResponse.success) {

                // Check if registrations are currently being held
                if (appConfig.registrationMode === "hold") {

                    // Display the held registration confirmation
                    DisplayConfirmationDialog(

                        // Dialog message
                        "Thanks for registering. We've saved your registration and we'll email you as soon as StrichSuite is ready.",

                        // Confirm button title
                        "OK",

                        // Cancel button title - empty because we don't want a cancel button
                        ""

                    );

                    // Do not send the user to the login page because the account cannot be activated yet
                    return;

                }

                // Display the normal activation confirmation
                DisplayConfirmationDialog(

                    // Dialog message
                    "We've sent you an activation email. Be sure to check your spam folder.",

                    // Confirm button title
                    "OK",

                    // Cancel button title - empty because we don't want a cancel button
                    "",

                    // Returned response from the dialog box
                    function (is_confirmed) {

                        // Check if the response is TRUE
                        if (is_confirmed) {

                            // Set the prefill email cookie
                            SetCookie("prefill_email", formValues.email, 1);

                            // Navigate to the login page
                            navigate("/login");

                        }
                    }
                );

            }
        }

    }, [apiResponse, appConfig.registrationMode, formValues.email, navigate]);



    return (

        <div>

            <form onSubmit={handleSubmit}>

                {/* Bot detection honeypot - genuine users should never interact with this field */}
                <div style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "clip" }}>
                    <input
                        name="contact_website"
                        type="text"
                        value={formValues.contact_website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                    />
                </div>
                
                <input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formValues.email}
                    onChange={handleChange}
                />
                {fieldErrors.email && <p style={{ color: "red" }}>{fieldErrors.email}</p>}

                <br />
                <br />

                <div className={styles.login_password_input_wrap}>

                    <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={formValues.password}
                        onChange={handleChange}
                    />

                    <button
                        type="button"
                        className={styles.login_password_toggle}
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? "Hide password" : "Show password"}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                        <ShowAllIcon />
                    </button>

                </div>

                {fieldErrors.password && <p style={{ color: "red" }}>{fieldErrors.password}</p>}

                <br />
                <br />

                <div className={styles.login_password_input_wrap}>

                    <input
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm Password"
                        value={formValues.confirmPassword}
                        onChange={handleChange}
                    />

                    <button
                        type="button"
                        className={styles.login_password_toggle}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        title={showConfirmPassword ? "Hide password" : "Show password"}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                        <ShowAllIcon />
                    </button>

                </div>

                {fieldErrors.confirmPassword && <p style={{ color: "red" }}>{fieldErrors.confirmPassword}</p>}

                <br />
                <br />

                {formError && <p style={{ color: "red" }}>{formError}</p>}

                <CreateButton title={loading ? "Registering..." : "Register"} size="l" fullWidth="true" color="#1597e5" type="submit" disabled={loading} />

            </form>

            <br />

            <p>
                Already have an account? <span onClick={() => onSwitch("login")} style={{ cursor: "pointer" }}><b>Log In</b></span>
            </p>

        </div>

    );
};

export default RegisterForm;









